import { CalendarDays, MapPin, Users, ExternalLink, Compass, Moon, Sparkles, ArrowUpRight, CircleDot } from "lucide-react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { formatCHF, formatDate, formatTime } from "@/lib/api";
import { LieuActions } from "@/components/LieuActions";
import { eventPath } from "@/data/eventTypes";
import { circleTypeLabel, isPaganCircle, paganCircleBadge, paganCircleTitle } from "@/lib/cerclesPaiens";
import { useLocale } from "@/context/LocaleContext";

const TYPE_LABELS = { stage: "Stage de création", lieu_ancien: "Tambours des Lieux Anciens", cercle: "Cercle", formation: "Formation", voyage: "Voyage", soiree: "Soirée découverte", soin: "Soin collectif à distance", randonnee: "Randonnée & terrain", mystere_caquelon: "Mystère & caquelon", salon: "Marchés & salons" };
const TYPE_COLORS = { stage: "bg-terra text-earth", lieu_ancien: "bg-earth text-terra", cercle: "bg-sage text-sand", formation: "bg-wood text-sand", voyage: "bg-bark text-sand", soiree: "bg-sand-dark text-bark", soin: "bg-terra/20 text-earth border border-terra/50", randonnee: "bg-sage-light text-bark border border-sage/50", mystere_caquelon: "bg-terra/20 text-bark border border-terra/45", salon: "bg-wood/15 text-bark border border-wood/40" };

export const isSpeciale = (e) => !!e.edition_speciale && !e.annule && new Date(e.date_fin || e.date) >= new Date();
export const specialeFirst = (list) => [...list].sort((a, b) => (isSpeciale(b) ? 1 : 0) - (isSpeciale(a) ? 1 : 0) || new Date(a.date) - new Date(b.date));

export const estComplet = (e) => !e.places_illimitees && e.places_restantes <= 0;
export const placesTexte = (e) => {
  const inscrits = e.afficher_inscrits ? (e.inscrits ?? 0) : null;
  if (inscrits !== null) return inscrits > 0 ? `${inscrits} personne${inscrits > 1 ? "s" : ""} inscrite${inscrits > 1 ? "s" : ""}` : "Inscriptions ouvertes";
  if (e.places_illimitees) return "Places illimitées";
  return estComplet(e) ? "Complet" : `${e.places_restantes} place${e.places_restantes > 1 ? "s" : ""}`;
};

export const EventCard = ({ event, onBook, index = 0 }) => {
  const { locale } = useLocale();
  const complet = estComplet(event);
  const placesLabel = placesTexte(event);
  const annule = !!event.annule;
  const isVoyage = event.type === "voyage";
  const isSoin = event.type === "soin";
  const isSalon = event.type === "salon";
  const isGourmande = event.type === "mystere_caquelon";
  const speciale = isSpeciale(event);
  const id8 = event.id.slice(0, 8);
  const pagan = isPaganCircle(event);
  const paganTitle = paganCircleTitle(event, locale);
  const paganBadge = paganCircleBadge(event, locale);
  return (
    <motion.article
      initial={{ opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.7, delay: (index % 4) * 0.08, ease: [0.22, 1, 0.36, 1] }}
      id={`event-${id8}`}
      className={`relative rounded-2xl overflow-hidden flex flex-col hover:-translate-y-1 hover:shadow-lg transition-all duration-500 scroll-mt-28 ${
        speciale ? "bg-earth grain text-sand border border-terra/60 ring-1 ring-terra/30 shadow-lg shadow-earth/20 hover:shadow-terra/20" : "bg-card border border-sand-dark hover:shadow-earth/5"
      } ${annule ? "opacity-75" : ""}`}
      data-testid={`event-card-${id8}`}
      data-speciale={speciale || undefined}
      data-annule={annule || undefined}
    >
      {annule && (
        <div className="relative z-[2] flex items-center gap-2 px-6 lg:px-7 pt-5" data-testid={`event-annule-${id8}`}>
          <span className="font-mono text-[10px] uppercase tracking-[0.3em] px-3 py-1.5 rounded-full bg-bark text-sand">Annulé</span>
          <span className="text-xs text-fumee">Cet événement n'aura pas lieu — les inscriptions sont fermées.</span>
        </div>
      )}
      {speciale && (
        <div className="relative z-[2] flex items-center gap-2 px-6 lg:px-7 pt-5" data-testid={`event-speciale-${id8}`}>
          <Moon className="w-3.5 h-3.5 text-terra" />
          <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-terra">Édition spéciale · événement signature</span>
        </div>
      )}
      {event.reserve_parcours && (
        <div className="relative z-[2] flex items-center gap-2 px-6 lg:px-7 pt-5" data-testid={`event-wyrd-reserved-${id8}`}>
          <Compass className="w-3.5 h-3.5 text-terra" />
          <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-terra">Réservé aux membres Wyrd actifs</span>
        </div>
      )}
      {pagan && <div className="relative z-[2] flex items-center gap-2 px-6 lg:px-7 pt-5" data-testid={`event-pagan-circle-${id8}`}><CircleDot className="w-3.5 h-3.5 text-terra" /><span className="font-mono text-[10px] uppercase tracking-[0.18em] text-terra">{paganBadge}</span></div>}
      {(isVoyage || isGourmande) && event.image && (
        <div className="relative aspect-[16/9] overflow-hidden">
          <img src={event.image} alt={event.titre} loading="lazy" className="w-full h-full object-cover" />
          {event.statut_label && (
            <span className="absolute bottom-3 left-3 font-mono text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 rounded-full bg-sand/90 text-bark backdrop-blur" data-testid={`event-statut-${id8}`}>
              {event.statut_label}
            </span>
          )}
        </div>
      )}
      <div className={`relative z-[2] p-6 lg:p-7 flex flex-col gap-4 flex-1 ${speciale ? "pt-4" : ""}`}>
        <div className="flex items-center justify-between">
          <span className={`font-mono text-[10px] uppercase tracking-[0.25em] px-3 py-1.5 rounded-full ${TYPE_COLORS[event.type]}`}>
            {event.type === "cercle" ? circleTypeLabel(event, locale) : TYPE_LABELS[event.type]}
          </span>
          {isSalon && event.statut_label ? (
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 rounded-full border border-terra/60 text-terra" data-testid={`event-statut-${id8}`}>{event.statut_label}</span>
          ) : (
          <span className={`font-mono text-xs flex items-center gap-1.5 ${speciale ? "text-sand/60" : "text-fumee"}`} data-testid={`event-places-${id8}`}>
            <Users className="w-3.5 h-3.5" />
            {isVoyage ? "Petit groupe · 4 à 8" : isSalon ? "Stand Entre Les Mondes" : annule ? "—" : placesLabel}
          </span>
          )}
        </div>
        <h3 className={`font-serif leading-snug ${speciale ? "text-2xl lg:text-3xl text-sand" : "text-xl lg:text-2xl text-bark"} ${annule ? "line-through decoration-terra/60" : ""}`}>
          {event.slug ? <Link to={eventPath(event)} data-testid={`event-title-link-${id8}`} className="hover:text-terra transition-colors duration-300">{paganTitle}</Link> : paganTitle}
        </h3>
        <p className={`text-sm leading-relaxed line-clamp-3 ${speciale ? "text-sand/70" : "text-fumee"}`}>{event.description}</p>
        <div className={`mt-auto space-y-2 text-sm ${speciale ? "text-sand/75" : "text-fumee"}`}>
          <p className="flex items-center gap-2">
            <CalendarDays className="w-4 h-4 text-terra shrink-0" />
            <span className={event.date_label ? "" : "capitalize"}>{event.date_label || `${formatDate(event.date)} · ${formatTime(event.date)}`}</span>
            {(isVoyage || speciale) && event.duree && <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-terra ml-1">· {event.duree}</span>}
          </p>
          <p className="flex items-center gap-2">
            {isSoin ? <Sparkles className="w-4 h-4 text-terra shrink-0" /> : <MapPin className="w-4 h-4 text-terra shrink-0" />}
            {event.lieu}
            {isSoin && event.duree && <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-terra ml-1">· {event.duree}</span>}
          </p>
          {!isSoin && !/secret/i.test(event.lieu || "") && <LieuActions event={event} className="pl-6" />}
          {event.slug && (
            <Link to={eventPath(event)} data-testid={`event-fiche-${id8}`} className={`inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-[0.2em] transition-colors duration-300 ${speciale ? "text-terra hover:text-sand" : "text-terra hover:text-bark"}`}>
              Voir la fiche <ArrowUpRight className="w-3.5 h-3.5" />
            </Link>
          )}
        </div>
        <div className={`flex items-center justify-between pt-4 border-t ${speciale ? "border-sand/15" : "border-sand-dark"}`}>
          {event.prix != null ? (
            <span className="font-serif text-lg text-terra">{isVoyage && <span className="font-mono text-[10px] uppercase tracking-[0.15em] mr-1">dès</span>}{formatCHF(event.prix)}</span>
          ) : (
            <span className="font-mono text-[11px] uppercase tracking-[0.15em] text-sage">
              {event.reserve_parcours ? "Réservé aux membres du Wyrd" : event.prix_label ? event.prix_label : isSalon ? "Entrée libre" : event.type === "cercle" ? "Tarif communiqué" : "Tarif sur demande"}
            </span>
          )}
          {annule ? (
            <span data-testid={`event-annule-btn-${id8}`} className="px-5 py-2.5 rounded-full text-sm tracking-wide bg-sand-dark text-fumee cursor-not-allowed">Annulé</span>
          ) : event.reserve_parcours ? (
            <button onClick={() => onBook(event)} data-testid={`event-wyrd-${id8}`}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm tracking-wide bg-earth text-sand hover:bg-terra hover:text-earth transition-colors duration-300">
              Membres du Wyrd <Compass className="w-3.5 h-3.5" />
            </button>
          ) : isSalon ? (
            event.lien_externe ? (
              <a href={event.lien_externe} target="_blank" rel="noopener noreferrer" data-testid={`event-salon-${id8}`}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm tracking-wide whitespace-nowrap border border-wood/30 text-bark hover:border-terra hover:text-terra transition-colors duration-300">
                Site de l'événement <ExternalLink className="w-3.5 h-3.5" />
              </a>
            ) : null
          ) : isVoyage ? (
            <a href={event.lien_externe} target="_blank" rel="noopener noreferrer" data-testid={`event-voyage-${id8}`}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm tracking-wide bg-earth text-sand hover:bg-terra hover:text-earth transition-colors duration-300">
              Se préinscrire <ExternalLink className="w-3.5 h-3.5" />
            </a>
          ) : (
            <button
              onClick={() => onBook(event)}
              disabled={complet}
              data-testid={`event-book-${id8}`}
              className={`px-5 py-2.5 rounded-full text-sm tracking-wide transition-colors duration-300 ${
                complet ? "bg-sand-dark text-fumee cursor-not-allowed" : speciale ? "bg-terra text-earth hover:bg-sand" : "bg-earth text-sand hover:bg-terra hover:text-earth"
              }`}
            >
              {complet ? "Complet" : isSoin ? "Participer" : "Réserver"}
            </button>
          )}
        </div>
      </div>
    </motion.article>
  );
};
